import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Screen } from '@ghostscreen/contracts';
import { apiErrorMessage, formatNumber } from '../../../i18n';
import { api } from '../../../lib/api';
import { getPlayerPublicUrl } from '../../../lib/player/getPlayerPublicUrl';
import { ManualPairingCode } from './ManualPairingCode';
import { PairingQrScanner } from './scan/PairingQrScanner';

const PLAYER_URL = getPlayerPublicUrl();
const PLAYER_HOST = new URL(PLAYER_URL).host;

type DisplayPairingSectionProps = {
  organisationId: string;
  screen: Screen;
  unpaired: boolean;
};

/**
 * TV-first pairing: open the player on the TV, scan its QR in-app (or with the phone
 * camera), and this display connects. Typing a dashboard code into the TV is a fallback.
 */
export function DisplayPairingSection({
  organisationId,
  screen,
  unpaired,
}: DisplayPairingSectionProps) {
  const { t } = useTranslation('displays');
  const queryClient = useQueryClient();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [connectedNote, setConnectedNote] = useState<string | null>(null);

  const approve = useMutation({
    mutationFn: (code: string) =>
      api.approvePairingSession(organisationId, code, { screenId: screen.id }),
    onSuccess: () => {
      setScannerOpen(false);
      setCodeOpen(false);
      setConnectedNote(t('pairing.connected'));
      void queryClient.invalidateQueries({ queryKey: ['screens', organisationId] });
    },
  });

  const approveError = approve.isError
    ? apiErrorMessage(approve.error, t('pairing.connectError'))
    : null;

  const steps = [
    <Trans
      t={t}
      i18nKey="pairing.steps.openPlayer"
      values={{ host: PLAYER_HOST }}
      components={{
        playerLink: (
          <a
            href={PLAYER_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold text-ink underline underline-offset-2"
          />
        ),
      }}
    />,
    <>{t('pairing.steps.scan')}</>,
    <Trans
      t={t}
      i18nKey="pairing.steps.autoConnect"
      values={{ name: screen.name }}
      components={{ strong: <span className="font-semibold text-ink" /> }}
    />,
  ];

  return (
    <section className="space-y-3">
      <h3 className="m-0 text-sm font-bold text-ink">
        {unpaired ? t('pairing.headingConnect') : t('pairing.headingMove')}
      </h3>
      {!unpaired ? <p className="m-0 text-sm text-muted">{t('pairing.moveHint')}</p> : null}
      <ol className="m-0 list-none space-y-2.5 p-0">
        {steps.map((step, index) => (
          <li key={index} className="flex gap-3 text-sm text-muted">
            <span
              aria-hidden
              className="grid h-6 w-6 shrink-0 place-items-center rounded-full border border-line text-xs font-bold text-ink"
            >
              {formatNumber(index + 1)}
            </span>
            <span className="pt-0.5">{step}</span>
          </li>
        ))}
      </ol>

      {unpaired ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setConnectedNote(null);
              setCodeOpen(false);
              approve.reset();
              setScannerOpen(true);
            }}
            className="min-w-0 flex-1 rounded-[10px] px-3 py-2.5 text-sm font-bold text-[var(--gs-accent-ink)]"
            style={{ background: 'var(--gs-accent-solid)' }}
          >
            {t('pairing.scanButton')}
          </button>
          <button
            type="button"
            onClick={() => {
              setConnectedNote(null);
              setCodeOpen((open) => !open);
            }}
            className="min-w-0 flex-1 rounded-[10px] border border-line px-3 py-2.5 text-sm font-semibold text-ink hover:border-[var(--gs-accent)]"
            aria-expanded={codeOpen}
          >
            {t('pairing.cantScan')}
          </button>
        </div>
      ) : null}

      {connectedNote ? (
        <p className="m-0 text-sm font-semibold text-ink" role="status">
          {connectedNote}
        </p>
      ) : null}

      {unpaired && codeOpen ? (
        <ManualPairingCode
          key={`${screen.id}-code`}
          organisationId={organisationId}
          screenId={screen.id}
          autoRequest
        />
      ) : null}

      <PairingQrScanner
        open={scannerOpen}
        screenName={screen.name}
        busy={approve.isPending}
        approveError={approveError}
        onCode={(code) => approve.mutate(code)}
        onClose={() => {
          if (approve.isPending) return;
          setScannerOpen(false);
          approve.reset();
        }}
      />
    </section>
  );
}
